import React, { useMemo } from 'react';
import { type AITTheme, useAITTheme } from '@this/shared/ui/theme';

import type SelectStore from '@this/src/domain/select_store';
import type SelectRepository from '@this/src/domain/select_repository';
import type SearchQuery from '@this/domain/search_query';
import type SearchQueryItem from '@this/domain/search_query_item';
import type TransitList from '@this/domain/transit/transit_list';

import SelectListAreaSearchTemplate from '../list_area_search/list_area_search.template';
import TransportListShortDistance from '../transport_list/TransportListShortDistance';
import FlightCondition from '../../flight_condition/FlightCondition';

interface TransportListAreaProps {
  query: SearchQuery;
  store: SelectStore;
  repository: SelectRepository;
  icon: string;
  listWrapper: TransitList;
  queryItem?: SearchQueryItem;
  direction?: string;
  showSearchBox: boolean;
  toggleShowSearchBox: () => void;
  transportSegment: number | undefined;
  isOpenFlightCondition: boolean;
  errors: string[];
  handleTransportSegmentClick: (i: number) => () => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setItemLoading: (loading: boolean) => void;
  handleFlightConditionSubmit: (
    filteredUpgradeSeat: boolean,
    carrierIds: string[],
    changeablePrices: string[],
    showConnectingAir: boolean
  ) => void;
  openFlightCondition: () => void;
  closeFlightCondition: () => void;
  isPcLayout?: boolean;
}

const TransportListArea: React.FC<TransportListAreaProps> = ({
  query,
  store,
  repository,
  icon,
  listWrapper,
  queryItem,
  direction,
  showSearchBox,
  toggleShowSearchBox,
  handleSubmit,
  setItemLoading,
  handleFlightConditionSubmit,
  openFlightCondition,
  closeFlightCondition,
  isOpenFlightCondition,
  transportSegment,
  handleTransportSegmentClick,
  errors,
  isPcLayout = false
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme, isPcLayout), [theme, isPcLayout]);

  if (listWrapper === null) return null;

  return (
    <div style={styles.container}>
      {store.result.type === 'separate' &&
        (store.result.isRoundTripSearchResult() || store.result.isMultipleLocationsSearchResult()) &&
        store.result.items.length > 0 &&
        query &&
        queryItem && (
          <SelectListAreaSearchTemplate
            showSearchBox={showSearchBox}
            toggleShowSearchBox={toggleShowSearchBox}
            handleSubmit={handleSubmit}
            queryItem={queryItem}
            query={query}
            errors={errors}
          />
        )}

      <div style={styles.header}>
        <span>
          <div style={styles.icon}>{icon}</div>
          <div style={styles.title}>
            <span>すべての候補(</span>
            <span data-wovn-ignore>{listWrapper.length()}</span>
            <span>件)</span>
          </div>
        </span>
        {!listWrapper.package && (
          <>
            <div style={styles.tabButtons}>
              <button
                type="button"
                className="select-list-area__header__button transport-list-area__tab-button"
                style={styles.tabButton(transportSegment === 0)}
                onClick={handleTransportSegmentClick(0)}
                data-active={transportSegment === 0}
              >
                飛行機
              </button>
              <button
                type="button"
                className="select-list-area__header__button transport-list-area__tab-button"
                style={styles.tabButton(transportSegment === 1)}
                onClick={handleTransportSegmentClick(1)}
                data-active={transportSegment === 1}
              >
                鉄道
              </button>
            </div>
            {transportSegment === 0 && (
              <div style={styles.flightConditions}>
                <button type="button" style={styles.conditionLink} onClick={openFlightCondition}>
                  <img style={styles.conditionIconImg} src="/images/filter_icon.png" alt="" />
                  絞り込み
                </button>
              </div>
            )}
          </>
        )}

        {store.result.type === 'jrPackage' && (
          <span style={styles.small}>
            ※パッケージ商品の新幹線チケットは日時の変更が一切できません
            <br />
            通常の個別新幹線チケットとは異なる種類のチケットですのでご注意ください
          </span>
        )}
      </div>

      {listWrapper.shinkansenVacancyEnabled && !listWrapper.package && transportSegment === 1 && (
        <div style={styles.vacancyLegend}>○・・・残席あり、△・・・残席わずか、×・・・残席なし(選択できません)</div>
      )}

      {!listWrapper.package && (
        <FlightCondition
          queryItem={queryItem}
          listWrapper={listWrapper}
          showUpgradeSeatBox={store.result.query.firstTravelerDomesticAirSeatUpgrade()}
          open={isOpenFlightCondition}
          onSubmit={handleFlightConditionSubmit}
          onAbort={closeFlightCondition}
        />
      )}

      <div style={styles.body}>
        <TransportListShortDistance
          queryItem={queryItem}
          transits={listWrapper}
          repository={repository}
          store={store}
          onChange={store.setTab('none')}
          direction={direction}
          parentLoading={setItemLoading}
        />
      </div>

      <div style={styles.footer} />
    </div>
  );
};

const getStyles = (theme: AITTheme, isPcLayout: boolean) => ({
  container: isPcLayout
    ? ({
        flex: '1 1 auto',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden'
      } as const)
    : ({} as const),
  header: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'space-between',
    marginBottom: 10,
    gap: isPcLayout ? 10 : undefined,
    flexShrink: isPcLayout ? 0 : undefined
  },
  icon: {
    display: 'inline-block',
    textAlign: 'center' as const,
    width: 20,
    height: 20,
    background: theme.iconColor,
    borderRadius: 10,
    color: 'white',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  title: {
    display: 'inline-block',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  footer: {
    marginTop: 10,
    flexShrink: 0
  },
  tabButtons: {
    display: 'flex',
    alignItems: 'center',
    width: isPcLayout ? 172 : ('100%' as const),
    flexBasis: isPcLayout ? undefined : ('100%' as const),
    marginTop: isPcLayout ? undefined : 12,
    boxSizing: 'border-box' as const,
    border: `1px solid ${theme.linkColor}`,
    borderRadius: theme.buttonRadius
  },
  tabButton: (active: boolean) => ({
    display: 'inline-block',
    padding: isPcLayout ? '5px 0' : '10px 0',
    backgroundColor: active ? theme.linkColor : '#fff',
    color: active ? '#fff' : theme.linkColor,
    borderRadius: 0,
    width: '50%',
    outline: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit'
  }),
  flightConditions: {
    display: 'flex',
    alignItems: isPcLayout ? 'flex-end' : undefined,
    justifyContent: 'space-between',
    flexBasis: '100%',
    fontSize: 12,
    fontWeight: 'bold' as const,
    marginTop: isPcLayout ? undefined : 10
  },
  conditionLink: {
    color: '#404040',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit'
  },
  conditionIconImg: {
    margin: 'auto 2px auto 0',
    verticalAlign: 'middle' as const
  },
  small: {
    fontSize: 10
  },
  body: isPcLayout
    ? ({
        flex: 1,
        minHeight: 0,
        overflowY: 'auto' as const,
        WebkitOverflowScrolling: 'touch' as const
      } as const)
    : ({
        display: 'flex',
        flexDirection: 'column' as const,
        maxHeight: '66vh',
        overflowY: 'scroll' as const,
        WebkitOverflowScrolling: 'touch' as const
      } as const),
  vacancyLegend: {
    fontSize: 10,
    color: theme.textColor,
    padding: '2px',
    lineHeight: 1.6,
    flexShrink: isPcLayout ? 0 : undefined
  }
});

export default TransportListArea;
